import { Blurs } from "Infrastructure/Themes/Blur";
import { colors } from "Infrastructure/Themes/Colors";
import { Shadows } from "Infrastructure/Themes/Shadows";
import { CustomLink } from "Shared/CustomLink";
import {
  createTheme,
  type Theme,
  type ThemeOptions,
} from "@mui/material/styles";
import { FontWeights } from "./FontWeights";
import { Radii } from "./Radius";
import { Spacings } from "./Spacing";
import { TypographyVariant } from "./Typography";
import { Widths } from "./Width";

export type Color = {
  text: {
    primary: string;
    primaryHover: string;
    secondary: string;
    secondaryHover: string;
    tertiary: string;
    tertiaryHover: string;
    onbrand: string;
    disabled: string;
    inverse: string;
    light: string;
    dark: string;
    brandPrimary: string;
    brandPrimaryHover: string;
    brandSecondary: string;
    rewardGold: string;
    info: string;
    success: string;
    warning: string;
    danger: string;
  };
  background: {
    primary: string;
    secondary: string;
    tertiary: string;
    inverse: string;
    overlay: string;
    hover: string;
    selected: string;
    disabled: string;
    brandPrimary: string;
    brandHover: string;
    brandSecondary: string;
    brandTertiary: string;
    infoPrimary: string;
    infoHover: string;
    infoSecondary: string;
    infoTertiary: string;
    successPrimary: string;
    successHover: string;
    successSecondary: string;
    successTertiary: string;
    warningPrimary: string;
    warningHover: string;
    warningSecondary: string;
    warningTertiary: string;
    dangerPrimary: string;
    dangerHover: string;
    dangerSecondary: string;
    dangerTertiary: string;
  };
  border: {
    primary: string;
    primaryHover: string;
    secondary: string;
    disabled: string;
    inverse: string;
    selected: string;
    brand: string;
    info: string;
    success: string;
    warning: string;
    danger: string;
  };
  utility: {
    categoryAdaptation: string;
    categoryTools: string;
    categoryBusiness: string;
    categoryLaw: string;
    categoryMarketing: string;
    categoryPersonalGrowth: string;
    categoryLeadership: string;
  };
};

export type ThemeColors = {
  light: Color;
  dark: Color;
};

const theme = (color: Color): Theme => {
  const theme: ThemeOptions = {
    customSpacing: Spacings,
    radius: Radii,
    width: Widths,
    colors: {
      text: {
        primary: color.text.primary,
        primaryHover: color.text.primaryHover,
        secondary: color.text.secondary,
        secondaryHover: color.text.secondaryHover,
        tertiary: color.text.tertiary,
        tertiaryHover: color.text.tertiaryHover,
        onbrand: color.text.onbrand,
        disabled: color.text.disabled,
        inverse: color.text.inverse,
        light: color.text.light,
        dark: color.text.dark,
        brandPrimary: color.text.brandPrimary,
        brandPrimaryHover: color.text.brandPrimaryHover,
        brandSecondary: color.text.brandSecondary,
        rewardGold: color.text.rewardGold,
        info: color.text.info,
        success: color.text.success,
        warning: color.text.warning,
        danger: color.text.danger,
      },
      background: {
        primary: color.background.primary,
        secondary: color.background.secondary,
        tertiary: color.background.tertiary,
        inverse: color.background.inverse,
        overlay: color.background.overlay,
        hover: color.background.hover,
        selected: color.background.selected,
        disabled: color.background.disabled,
        brandPrimary: color.background.brandPrimary,
        brandSecondary: color.background.brandSecondary,
        brandTertiary: color.background.brandTertiary,
        infoPrimary: color.background.infoPrimary,
        infoHover: color.background.infoHover,
        infoSecondary: color.background.infoSecondary,
        infoTertiary: color.background.infoTertiary,
        successPrimary: color.background.successPrimary,
        successHover: color.background.successHover,
        successSecondary: color.background.successSecondary,
        successTertiary: color.background.successTertiary,
        warningPrimary: color.background.warningPrimary,
        warningHover: color.background.warningHover,
        warningSecondary: color.background.warningSecondary,
        warningTertiary: color.background.warningTertiary,
        dangerPrimary: color.background.dangerPrimary,
        dangerHover: color.background.dangerHover,
        dangerSecondary: color.background.dangerSecondary,
        dangerTertiary: color.background.dangerTertiary,
      },
      border: {
        primary: color.border.primary,
        primaryHover: color.border.primaryHover,
        secondary: color.border.secondary,
        disabled: color.border.disabled,
        inverse: color.border.inverse,
        selected: color.border.selected,
        brand: color.border.brand,
        info: color.border.info,
        success: color.border.success,
        warning: color.border.warning,
        danger: color.border.danger,
      },
      utility: {
        categoryAdaptation: color.utility.categoryAdaptation,
        categoryTools: color.utility.categoryTools,
        categoryBusiness: color.utility.categoryBusiness,
        categoryLaw: color.utility.categoryLaw,
        categoryMarketing: color.utility.categoryMarketing,
        categoryPersonalGrowth: color.utility.categoryPersonalGrowth,
        categoryLeadership: color.utility.categoryLeadership,
      },
    },
    blurs: Blurs,
    customShadows: Shadows,
    customFontWeight: FontWeights,
    typography: {
      fontFamily: "'Space Grotesk', sans-serif",
      ...TypographyVariant(color.text.primary),
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: color.background.secondary,
          },
        },
      },
      MuiLink: {
        defaultProps: {
          component: CustomLink,
        },
      },
      MuiButtonBase: {
        defaultProps: {
          LinkComponent: CustomLink,
        },
      },
    },
  };

  return createTheme(theme);
};

export const getTheme = (mode: "light" | "dark" = "light") => {
  return theme(colors()[mode]);
};
